import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
    refreshToken,
    handleSessionExpired,
    logoutUser,
    getTokenRemainingMs,
    getTokenTotalDurationMs,
    isTokenExpired,
} from "../service/register.service";

// ============================================================================
// CONFIGURATION
// ============================================================================
// Total user inactivity limit (15 minutes)
export const DEFAULT_INACTIVITY_LIMIT_MS = 15 * 60 * 1000;

// Maximum warning countdown window in seconds (production cap: 120s = 2 min)
export const DEFAULT_WARNING_COUNTDOWN_SEC = 120;

/**
 * SessionTimeoutManager Component
 * ----------------------------------------------------------------------------
 * Dynamically scales countdown warnings to both production and short test configurations:
 * - If token total duration is 120s (2m), warning countdown is ~30s (last 25% of token life).
 * - If idle limit is 60s (1m), warning countdown is ~15s (last 25% of idle time).
 * - If token is 15m or 1h, warning countdown caps at 120s (2 minutes).
 * - Resets activity on login, route transitions, and user engagement.
 * - Refresh token is NEVER spammed in background.
 */
const SessionTimeoutManager = () => {
    const location = useLocation();
    const lastActivityRef = useRef(Date.now());
    const isModalOpenRef = useRef(false);
    const countdownIntervalRef = useRef(null);

    // Reset idle timer on every route transition (e.g. logging in and navigating to /payment)
    useEffect(() => {
        lastActivityRef.current = Date.now();
    }, [location.pathname]);

    // Format seconds into mm:ss (e.g. 15 -> "00:15", 120 -> "02:00")
    const formatTime = (totalSeconds) => {
        const m = Math.floor(totalSeconds / 60)
            .toString()
            .padStart(2, "0");
        const s = (totalSeconds % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    };

    // Resets activity timestamp on genuine user engagement
    const recordUserActivity = () => {
        // While modal is open, ignore events until user explicitly chooses an action
        if (isModalOpenRef.current) return;

        const now = Date.now();
        // Throttle to at most once per second for zero performance overhead
        if (now - lastActivityRef.current > 1000) {
            lastActivityRef.current = now;
        }
    };

    // Displays the inactivity countdown dialog
    const showCountdownWarning = (initialCountdownSec) => {
        if (isModalOpenRef.current) return;
        isModalOpenRef.current = true;

        let secondsRemaining = Math.max(5, Math.round(initialCountdownSec));
        const totalInitialSec = secondsRemaining;

        Swal.fire({
            icon: "warning",
            title: '<span style="color:#92400e; font-size:1.35rem; font-weight:800;">Session Expiration Warning</span>',
            html: `
                <div style="font-size:0.92rem; color:#475569; line-height:1.6; text-align:left; margin-top:4px;">
                    <p style="margin:0 0 10px;">
                        For your security and conference data protection, your session will automatically expire in:
                    </p>
                    <div style="margin:14px 0 10px; padding:14px 16px; background:#fffbeb; border:1.5px solid #fef3c7; border-left:5px solid #f59e0b; border-radius:10px; text-align:center;">
                        <div style="font-size:0.75rem; font-weight:800; text-transform:uppercase; letter-spacing:0.6px; color:#92400e;">
                            Auto Sign-Out Countdown
                        </div>
                        <div id="swal-inactivity-timer" style="font-size:2.2rem; font-weight:900; font-family:monospace; color:#b45309; margin-top:4px; letter-spacing:1px;">
                            ${formatTime(secondsRemaining)}
                        </div>
                        <div style="margin-top:10px; background:#fed7aa; height:6px; border-radius:3px; overflow:hidden;">
                            <div id="swal-inactivity-bar" style="background:#ea580c; height:100%; width:100%; transition:width 1s linear;"></div>
                        </div>
                        <div style="font-size:0.74rem; color:#b45309; margin-top:6px;">
                            Session will safely close when countdown hits 00:00
                        </div>
                    </div>
                    <p style="margin:0; font-size:0.83rem; color:#64748b;">
                        Click <strong>Stay Signed In</strong> below to continue your current session without interruption.
                    </p>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Stay Signed In",
            cancelButtonText: "Sign Out Now",
            confirmButtonColor: "#2b53a3",
            cancelButtonColor: "#64748b",
            allowOutsideClick: false,
            allowEscapeKey: false,
            focusConfirm: true,
            didOpen: () => {
                const timerElem = document.getElementById("swal-inactivity-timer");
                const timerBarElem = document.getElementById("swal-inactivity-bar");

                countdownIntervalRef.current = setInterval(async () => {
                    secondsRemaining -= 1;

                    if (timerElem) {
                        timerElem.textContent = formatTime(Math.max(0, secondsRemaining));
                    }
                    if (timerBarElem && totalInitialSec > 0) {
                        const pct = Math.max(0, Math.min(100, (secondsRemaining / totalInitialSec) * 100));
                        timerBarElem.style.width = pct + "%";
                    }

                    if (secondsRemaining <= 0) {
                        clearInterval(countdownIntervalRef.current);
                        countdownIntervalRef.current = null;
                        isModalOpenRef.current = false;
                        Swal.close();
                        await handleSessionExpired();
                    }
                }, 1000);
            },
            willClose: () => {
                if (countdownIntervalRef.current) {
                    clearInterval(countdownIntervalRef.current);
                    countdownIntervalRef.current = null;
                }
                isModalOpenRef.current = false;
            },
        }).then(async (result) => {
            if (result.isConfirmed) {
                // User clicked "Stay Signed In"
                try {
                    await refreshToken();
                    lastActivityRef.current = Date.now();

                    // Reassuring confirmation toast
                    Swal.fire({
                        toast: true,
                        position: "top-end",
                        icon: "success",
                        title: "Session extended successfully",
                        showConfirmButton: false,
                        timer: 2000,
                    });
                } catch {
                    await handleSessionExpired();
                }
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                // User clicked "Sign Out Now"
                await logoutUser();
                window.location.href = "/login?loggedOut=true";
            }
        });
    };

    useEffect(() => {
        // Activity events that indicate active engagement
        const activityEvents = ["mousedown", "keydown", "scroll", "touchstart"];

        activityEvents.forEach((eventName) => {
            window.addEventListener(eventName, recordUserActivity, { passive: true });
        });

        // Listen for login / logout state changes to reset idle timer
        const handleAuthChanged = (e) => {
            if (e?.detail?.isAuthenticated) {
                lastActivityRef.current = Date.now();
                if (isModalOpenRef.current) {
                    isModalOpenRef.current = false;
                    Swal.close();
                }
            }
        };
        window.addEventListener("ivec:auth-changed", handleAuthChanged);

        // Periodic idle and token expiration monitor (runs every 1 second, ZERO network calls)
        const checkInterval = setInterval(async () => {
            // Do not run on the public login page
            if (window.location.pathname === "/login") return;

            // Do not stack multiple modals
            if (isModalOpenRef.current) return;

            const token = localStorage.getItem("ivec_token");
            if (!token) return;

            const now = Date.now();
            const idleTimeMs = now - lastActivityRef.current;
            const remainingTokenMs = getTokenRemainingMs(token);
            const totalTokenDurationMs = getTokenTotalDurationMs(token);

            const idleLimitMs =
                window.__IVEC_IDLE_TIMEOUT_MS__ || DEFAULT_INACTIVITY_LIMIT_MS;

            // -----------------------------------------------------------------
            // DYNAMIC WARNING CALCULATION:
            // Prevents premature warning popup during test setups or short tokens!
            // - For token: warning window is at most 25% of total token duration
            //   (e.g., 30s for 120s test token, 120s for 15m/1h token).
            // - For idle: warning window is at most 25% of idle limit
            //   (e.g., 15s for 60s test idle limit, 120s for 15m idle limit).
            // -----------------------------------------------------------------
            const maxTokenWarningSec = Math.floor((totalTokenDurationMs / 1000) * 0.25);
            const tokenWarningSec = Math.min(
                DEFAULT_WARNING_COUNTDOWN_SEC,
                Math.max(10, maxTokenWarningSec)
            );
            const tokenWarningMs = tokenWarningSec * 1000;

            const maxIdleWarningSec = Math.floor((idleLimitMs / 1000) * 0.25);
            const idleWarningSec = Math.min(
                DEFAULT_WARNING_COUNTDOWN_SEC,
                Math.max(10, maxIdleWarningSec)
            );
            const idleWarningMs = idleWarningSec * 1000;
            const idleWarningThresholdMs = idleLimitMs - idleWarningMs;

            // -------------------------------------------------------------
            // 1. Inactivity Warning: User has been idle near the limit
            // -------------------------------------------------------------
            if (idleTimeMs >= idleWarningThresholdMs && idleTimeMs < idleLimitMs) {
                const countdownSec = Math.max(
                    5,
                    Math.ceil((idleLimitMs - idleTimeMs) / 1000)
                );
                showCountdownWarning(countdownSec);
                return;
            }

            // -------------------------------------------------------------
            // 2. Inactivity Exceeded (e.g. system sleep/laptop wake after long idle)
            // -------------------------------------------------------------
            if (idleTimeMs >= idleLimitMs) {
                await handleSessionExpired();
                return;
            }

            // -------------------------------------------------------------
            // 3. Token Approaching Expiration (within tokenWarningMs)
            // -------------------------------------------------------------
            if (remainingTokenMs > 0 && remainingTokenMs <= tokenWarningMs) {
                const countdownSec = Math.max(
                    5,
                    Math.ceil(remainingTokenMs / 1000)
                );
                showCountdownWarning(countdownSec);
                return;
            }

            // -------------------------------------------------------------
            // 4. Token Fully Expired
            // -------------------------------------------------------------
            if (isTokenExpired(token)) {
                await handleSessionExpired();
                return;
            }
        }, 1000);

        return () => {
            activityEvents.forEach((eventName) => {
                window.removeEventListener(eventName, recordUserActivity);
            });
            window.removeEventListener("ivec:auth-changed", handleAuthChanged);
            clearInterval(checkInterval);
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, []);

    return null;
};

export default SessionTimeoutManager;
